/* eslint-disable react/prop-types */
import currency from "currency.js";
import { useMemo } from "react";
import { useTable } from "react-table";
import { useTranslation } from "react-i18next";
import { CollateralAssetResponse } from "../../../codegen-api";
import { SPACING } from "../../../constants/design/spacing";
import {
  CONVERT_UI_ENABLED,
  TRANSFER_UI_ENABLED,
} from "../../../constants/featureFlags";
import { ITableColumn } from "../../../interfaces/Table/TableColumn";
import { getAssetLogo, getAssetShortName } from "../../../utils/asset/assets";
import { DefaultCellForColumn } from "../../shared/Table/DefaultCellForColumn";
import { DefaultHeaderForColumn } from "../../shared/Table/DefaultHeaderForColumn";
import { PortfolioTableWrapper } from "../Table/style";
import {
  CollateralContainer,
  CollateralRow,
  MobileActionButtonsContainer,
  MobileCollateralLogo,
  MobileCollateralRow,
} from "./style";
import { Button, ButtonThemeEnum } from "../../Buttons/styles";
import { Spinner } from "../../shared/Spinner";
import { SpinnerContainerWrapper } from "../../shared/Spinner/style";
import { ICollateralProps, ICollateralRow } from "./Token.types";
import { computeCollaterals } from "./Token.helpers";
import { getCollateralColumns } from "./Token.columns";
import { UNKNOWN_DISPLAY } from "../../../utils/unknownNumbers";

function Token({
  isMobileScreen,
  accountData,
  isLoading,
  onDeposit,
  onWithdraw,
  onTransfer,
  onConvert,
}: ICollateralProps) {
  const { t } = useTranslation("app", {
    keyPrefix: "PortfolioSettings.Collateral",
  });
  const collaterals: ICollateralRow[] = useMemo(
    () => computeCollaterals(accountData),
    [accountData?.collaterals]
  );

  const columns: ITableColumn<ICollateralRow>[] = useMemo(
    () =>
      getCollateralColumns({
        t,
        onDeposit,
        onWithdraw,
        onTransfer,
        onConvert,
      }),
    [onConvert, onDeposit, onTransfer, onWithdraw, t]
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable({
      columns,
      data: collaterals,
      defaultColumn: {
        Header: DefaultHeaderForColumn,
        Cell: DefaultCellForColumn,
      } as any,
      // Key rows by asset symbol (not array index) so the same row keeps
      // the same DOM element across re-fetches, preventing the <img> from
      // being remounted (and the icon flicker / position shift the user sees).
      getRowId: (row) => String(row.id),
    });
  if (isLoading && !accountData) {
    return (
      <CollateralContainer>
        {/* <ComponentSubtitle padding={`0 0 0 ${SPACING.three}px`}>
          {t("token_collateral")}
        </ComponentSubtitle> */}
        <SpinnerContainerWrapper $height={160}>
          <Spinner />
        </SpinnerContainerWrapper>
      </CollateralContainer>
    );
  }

  return (
    <CollateralContainer>
      {/* <ComponentSubtitle padding={`0 0 0 ${SPACING.three}px`}>
        {t("token_collateral")}
      </ComponentSubtitle> */}
      {isMobileScreen ? (
        <div>
          {collaterals.map((c) => (
            <MobileCollateralRow key={c.id}>
              <MobileCollateralLogo>
                <img src={getAssetLogo(c.asset)} alt={c.asset} />
                <div>
                  <span>{getAssetShortName(c.asset)}</span>
                  <span>
                    {/* currency(undefined) is "$0.00" -- an unknown balance
                        must dash here too, as it does on desktop. */}
                    {c.balance === undefined
                      ? UNKNOWN_DISPLAY
                      : currency(c.balance, { precision: 4 }).format({
                          symbol: "",
                        })}
                  </span>
                </div>
              </MobileCollateralLogo>
              <span>
                {c.value === undefined
                  ? UNKNOWN_DISPLAY
                  : currency(c.value).format()}
              </span>
            </MobileCollateralRow>
          ))}
          {/* Default to USDC */}
          <MobileActionButtonsContainer>
            <Button
              disabled={!onDeposit}
              buttonTheme={ButtonThemeEnum.NEUTRAL2}
              onClick={
                onDeposit
                  ? () => onDeposit(CollateralAssetResponse.Usdc)
                  : undefined
              }
            >
              {t("deposit")}
            </Button>
            <Button
              disabled={!onWithdraw}
              buttonTheme={ButtonThemeEnum.NEUTRAL2}
              onClick={
                onWithdraw
                  ? () => onWithdraw(CollateralAssetResponse.Usdc)
                  : undefined
              }
            >
              {t("withdraw")}
            </Button>
            {/* Transfer disabled from UI/UX — flip TRANSFER_UI_ENABLED to re-enable */}
            {TRANSFER_UI_ENABLED && (
              <Button
                disabled={!onTransfer}
                buttonTheme={ButtonThemeEnum.NEUTRAL2}
                onClick={
                  onTransfer
                    ? () => onTransfer(CollateralAssetResponse.Usdc)
                    : undefined
                }
              >
                {t("transfer")}
              </Button>
            )}
            {/* Convert disabled from UI/UX — flip CONVERT_UI_ENABLED to re-enable */}
            {CONVERT_UI_ENABLED && (
              <Button
                disabled={!onConvert}
                buttonTheme={ButtonThemeEnum.NEUTRAL2}
                onClick={
                  onConvert
                    ? () => onConvert(CollateralAssetResponse.Usdc)
                    : undefined
                }
              >
                {t("convert")}
              </Button>
            )}
          </MobileActionButtonsContainer>
        </div>
      ) : (
        <PortfolioTableWrapper
          disableHoverRow
          verticalSpacing={SPACING.two}
          style={{
            position: "relative",
            padding: 0,
          }}
        >
          <table {...getTableProps()}>
            <thead>
              {headerGroups.map((headerGroup) => {
                const { key, ...trProps } = headerGroup.getHeaderGroupProps();
                return (
                  <tr key={key} {...trProps}>
                    {headerGroup.headers.map((column: any) => {
                      const { key, ...thProps } = column.getHeaderProps();
                      return (
                        <th key={key} {...thProps} style={{ width: "15%" }}>
                          {column.render("Header")}
                        </th>
                      );
                    })}
                  </tr>
                );
              })}
            </thead>
            <tbody {...getTableBodyProps()}>
              {rows.map((row) => {
                prepareRow(row);
                const { key, ...rowProps } = row.getRowProps();
                return (
                  <CollateralRow key={key} {...rowProps}>
                    {row.cells
                      .filter(({ column }) => column.id !== "size")
                      .map((cell) => {
                        const colSpan =
                          cell.column.id === "direction" ? 2 : undefined;
                        const { key, ...cellProps } = cell.getCellProps();
                        return (
                          <td key={key} {...cellProps} colSpan={colSpan}>
                            {cell.render("Cell")}
                          </td>
                        );
                      })}
                  </CollateralRow>
                );
              })}
            </tbody>
          </table>
        </PortfolioTableWrapper>
      )}
    </CollateralContainer>
  );
}

export default Token;
